import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Spinner,
  UncontrolledAccordion,
  UncontrolledDropdown,
} from "reactstrap";
import { Pill } from "./style";
import React, { useEffect, useState } from "react";
import { CustomizationSpinnerContainer, Notification } from "../common/style";
import { getOrderInfo, getSignedToken } from "../project/service";
import { useOutletContext, useParams } from "react-router-dom";
import { generateRequestUri } from "../common/service";

const NewOrder = ({ projectSummary: projectSummaryFromProps }) => {
  const outletContext = useOutletContext() || {};
  const projectSummary =
    outletContext.projectSummary || projectSummaryFromProps;
  const [order, setOrder] = useState({});
  const [addOns, setAddOns] = useState({});
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const params = useParams();
  const projectId = params.projectId;
  const resourceMapper = {
    order_summary: "download_order_summary",
    order_detailed_summary: "download_order_detailed",
  };

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const loadOrder = () => {
    setIsLoading(true);

    getOrderInfo({ projectId })
      .then((response) => {
        setOrder(response);
        setAddOns(response.add_ons);
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadOrder();
  }, [projectSummary.is_price_stale]);

  const downloadOrderInfo = (resource) => {
    setIsLoading(true);

    getSignedToken({ projectId, resource })
      .then(({ token }) =>
        window.open(
          generateRequestUri({
            url: `projects/${projectId}/${resourceMapper[resource]}`,
            params: { signed: token },
          }),
          "_blank",
          "noreferrer"
        )
      )
      .catch(({ error }) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      {isLoading ? (
        <CustomizationSpinnerContainer top="100px">
          <Spinner />
        </CustomizationSpinnerContainer>
      ) : (
        Object.keys(order).length > 0 && (
          <div className="order-summary">
            <div
              className="d-flex justify-content-between align-items-center border-bottom px-3"
              style={{ paddingBlock: "10px" }}
            >
              <div className="d-flex align-items-center column-gap-1">
                <p className="fw-bold fs-6 m-0">Order</p>
                <p className="m-0 grey_letter mt-1">{`(${order.modules_count} modules)`}</p>
                {order.status ? (
                  <Pill className="text-capitalize">{order.status}</Pill>
                ) : (
                  <div></div>
                )}
              </div>

              {order.price_access && (
                <UncontrolledDropdown>
                  <DropdownToggle className="border-0 order-download pointer">
                    <i className="fa-solid fa-download fs-6 pointer text-secondary" />
                  </DropdownToggle>
                  <DropdownMenu className="py-1">
                    <DropdownItem
                      onClick={() => downloadOrderInfo("order_summary")}
                    >
                      PO View
                    </DropdownItem>
                    <DropdownItem
                      onClick={() =>
                        downloadOrderInfo("order_detailed_summary")
                      }
                    >
                      Detailed View
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              )}
            </div>
            {order.status_additional_info && (
              <div className="border-bottom px-3 py-3 text-secondary">
                <span className="fa fa-info-circle me-1"></span>
                {order.status_additional_info}
              </div>
            )}

            <div className="p-3 fh">
              <UncontrolledAccordion stayOpen>
                {order.rooms.map((room, index) => (
                  <AccordionItem key={index}>
                    <AccordionHeader targetId={room.id}>
                      <div className="d-flex flex-row-reverse">
                        <p className="m-0 ms-2 mt-1 grey_letter">
                          {`(${room.modules_count} modules)`}
                        </p>
                        <p className="m-0 fs-14px">{room.name}</p>
                      </div>
                      <div className="position-absolute end-0">
                        <p className="m-0 active-dimension">
                          {room.price &&
                            `₹${room.price.toLocaleString("en-IN")}`}
                        </p>
                      </div>
                    </AccordionHeader>
                    <AccordionBody accordionId={room.id}>
                      {room.groups.map((group, index) => (
                        <AccordionItem key={index}>
                          <AccordionHeader targetId={room.id + "_" + group.id}>
                            <div className="d-flex flex-row-reverse">
                              <p className="m-0 ms-2 mt-1 grey_letter">
                                {`(${group.sqft} sq.ft)`}
                              </p>
                              <p className="m-0 fs-14px">{group.name}</p>
                            </div>
                            <div className="position-absolute end-0">
                              <p className="m-0">
                                {group.price &&
                                  `₹${group.price.toLocaleString("en-IN")}`}
                              </p>
                            </div>
                          </AccordionHeader>
                          <AccordionBody accordionId={room.id + "_" + group.id}>
                            {group.modules.map((module, index) => (
                              <div
                                key={index}
                                className="d-flex justify-content-between"
                              >
                                <p className="m-0 fs-14px">{module.name}</p>
                                <div className="d-flex align-items-center column-gap-2">
                                  {module.is_discounted && (
                                    <p className="m-0 grey_letter text-decoration-line-through">
                                      {module.original_price &&
                                        `₹${module.original_price.toLocaleString(
                                          "en-IN"
                                        )}`}
                                    </p>
                                  )}
                                  <p className="m-0 text-secondary">
                                    {module.price &&
                                      `₹${module.price.toLocaleString(
                                        "en-IN"
                                      )}`}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </AccordionBody>
                        </AccordionItem>
                      ))}

                      {room.modules.map((module, index) => (
                        <div
                          key={index}
                          className="d-flex justify-content-between"
                        >
                          <p className="m-0 fs-14px">{module.name}</p>
                          <div className="d-flex align-items-center column-gap-2">
                            {module.is_discounted && (
                              <p className="m-0 grey_letter text-decoration-line-through">
                                {module.original_price &&
                                  `₹${module.original_price.toLocaleString(
                                    "en-IN"
                                  )}`}
                              </p>
                            )}
                            <p className="m-0">
                              {module.price &&
                                `₹${module.price.toLocaleString("en-IN")}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </AccordionBody>
                  </AccordionItem>
                ))}
                {addOns.length > 0 && (
                  <AccordionItem key="addOns">
                    <AccordionHeader targetId="addOns">
                      <div className="d-flex flex-row-reverse">
                        <p className="m-0 fs-14px">Add-Ons</p>
                      </div>
                      <div className="position-absolute end-0">
                        <p className="m-0 active-dimension">
                          ₹
                          {addOns
                            .reduce(
                              (sum, addOn) => sum + Number(addOn.price || 0),
                              0
                            )
                            .toLocaleString("en-IN")}{" "}
                        </p>
                      </div>
                    </AccordionHeader>
                    <AccordionBody accordionId="addOns">
                      {addOns.map((addOn) => (
                        <div
                          key={addOn.id}
                          className="d-flex justify-content-between"
                        >
                          <p className="m-0 fs-14px">{addOn.item}</p>
                          <div className="d-flex align-items-center column-gap-2">
                            <p className="m-0 text-secondary">
                              {`₹${addOn.price.toLocaleString("en-IN")}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </AccordionBody>
                  </AccordionItem>
                )}
              </UncontrolledAccordion>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default NewOrder;
